import { Fragment, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { pierValidationSchema } from '@/utils/validations';
import { ErrorApiResponse } from '@/types/api.types';
import { PierModal } from './PierModal';
import {
  useCreatePierMutation,
  useUploadPierImageMutation,
} from '@/services/PierManagementService';
import { PierListings } from './PiersListings';
import { usePermissions } from '@/hooks/usePermissions';
import {
  IPierFormData,
  CreatePierPayload,
  PierContentTranslations,
} from '@/types/pier-management.types';

const serializeTranslations = (
  translations?: PierContentTranslations | null
): PierContentTranslations | null => {
  const result: PierContentTranslations = {};
  (['fi', 'sv', 'en', 'de'] as const).forEach((language) => {
    result[language] = translations?.[language]?.trim() || null;
  });
  return result;
};

const PierManagement = () => {
  const { currentLayout } = useLayout();
  const { hasPermission } = usePermissions();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState<IPierFormData>({
    name: '',
    harbor_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { enqueueSnackbar } = useSnackbar();
  const [createPier, { isLoading: isCreating }] = useCreatePierMutation();
  const [uploadPierImage, { isLoading: isUploadingImage }] =
    useUploadPierImageMutation();

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setFormData({
      name: '',
      harbor_id: '',
    });
    setErrors({});
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormData({ name: '', harbor_id: '' });
    setErrors({});
  };

  const handleSubmit = async () => {
    try {
      await pierValidationSchema.validate(formData, {
        abortEarly: false,
      });
      const payload: CreatePierPayload = {
        name: formData.name,
        harbor_id: formData.harbor_id,
        boater_label_translations: serializeTranslations(
          formData.boater_label_translations
        ),
        approach_note_translations: serializeTranslations(
          formData.approach_note_translations
        ),
        capacity_mode: formData.capacity_mode || undefined,
        pooled_capacity:
          formData.capacity_mode === 'POOLED'
            ? formData.pooled_capacity
            : undefined,
      };
      const created = await createPier(payload).unwrap();

      if (formData.image instanceof File) {
        try {
          await uploadPierImage({
            pier_id: created.data.id,
            file: formData.image,
            captions: formData.image_captions,
          }).unwrap();
        } catch {
          enqueueSnackbar(
            'Pier created, but image upload failed. Edit the pier to add an image.',
            { variant: 'error' }
          );
          handleCloseCreateModal();
          return;
        }
      }

      enqueueSnackbar('Pier created successfully', {
        variant: 'success',
      });
      handleCloseCreateModal();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message || 'Failed to create pier';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Piers Management" />
              <ToolbarDescription>
                You can manage your piers here.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {hasPermission('berth_pier_management_create') && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={handleOpenCreateModal}
                  aria-label="Add new pier"
                  data-testid="add-new-pier"
                >
                  Add New Pier
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}
      <Container>
        <PierListings />
      </Container>
      <PierModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        mode="create"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isSubmitting={isCreating || isUploadingImage}
      />
    </Fragment>
  );
};

export { PierManagement };
